import React, { useEffect, useState } from 'react';
import { FormFields } from '../form/Form';
import { HeaderBanner } from '../header-banner/HeaderBanner';
import { Box, Grid, Typography } from '@mui/material';
import { CalculatorTotals } from '../calculator-totals/CalculatorTotals';
import { Summary } from '../summary/Summary';
import { useStoreState } from '../../utils/utils';
import { RevenueCalculatorStore } from './RevenueCalculator.store';
import { LocalStoreProvider } from '../local-store-provider/LocalStoreProvider';
import { Dials } from '../dials/Dials';
import { Benchmarks } from '../benchmarks/Benchmarks';
import { getSummaryMessage } from '../summary/Summary.utils';
import { APP_MARGIN, COLORS_LIGHTGRAYBG } from '../../App.constants';
import { CalculatorTotalsMobile } from '../calculator-totals/CalculatorTotalsMobile';
import { DialsMobile } from '../dials/DialsMobile';
import { BenchmarksMobile } from '../benchmarks/BenchmarksMobile';
import { observer } from 'mobx-react';

const getMargin = (width: number) => {
  if (width < 800) {
    return 1;
  }

  if (width > 1000) {
    return APP_MARGIN + 10;
  }

  return APP_MARGIN;
};

const getWindowSize = () => {
  return window.innerWidth;
};

export const RevenueCalculator: React.FC = observer(() => {
  const localStore = useStoreState(() => new RevenueCalculatorStore());

  const [windowWidth, setWindowWidth] = useState<number>(getWindowSize());
  const [margin, setMargin] = useState<number>(getMargin(windowWidth));

  const onResize = () => {
    const windowSize = getWindowSize();
    const margin = getMargin(windowSize);
    setWindowWidth(windowSize);
    setMargin(margin);
  };

  useEffect(() => {
    onResize();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <LocalStoreProvider localStore={localStore}>
      <Grid
        container={true}
        justifyContent={'center'}
        position={'relative'}
        paddingY={2}
        paddingTop={6}
        bgcolor={COLORS_LIGHTGRAYBG}
      >
        <Box paddingX={margin}>
          <Typography
            fontSize={'2.25rem'}
            component={'h1'}
          >
            How Does Your Revenue Compare to Benchmarks?
          </Typography>
          <Typography
            fontSize={'1.125rem'}
            component={'h3'}
            marginBottom={4}
          >
            Calculate your revenue capacity with a few simple inputs below. See the revenue potential in your business
            and find out how adjustments to utilization, bill rates, and even headcount can drive stronger performance.
          </Typography>
          <HeaderBanner
            title={"What's your current revenue capacity?"}
            icon={'money'}
          />

          <FormFields windowWidth={windowWidth} />

          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <CalculatorTotals />
          </Box>

          <Box
            id={'mobile calc'}
            width={'100%'}
            sx={{ display: { xs: 'block', sm: 'none', marginBottom: '50px' } }}
          >
            <CalculatorTotalsMobile />
          </Box>

          <HeaderBanner
            title={getSummaryMessage(localStore.unCapturedRevenue)}
            icon={'info'}
            paddingX={4}
          />

          <Summary />
        </Box>

        <Box sx={{ display: { xs: 'block', sm: 'block', md: 'none' } }}>
          <DialsMobile />
        </Box>

        <Box
          sx={{ display: { xs: 'none', sm: 'none', md: 'block' } }}
          position={'relative'}
          height={'400px'}
          width={'100%'}
        >
          <Dials padding={margin} />
        </Box>

        <Box
          paddingX={margin}
          width={'100%'}
        >
          <Box sx={{ display: { xs: 'block', sm: 'block', md: 'block', lg: 'none' } }}>
            <BenchmarksMobile />
          </Box>

          <Box sx={{ display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' } }}>
            <Benchmarks />
          </Box>
        </Box>
      </Grid>
    </LocalStoreProvider>
  );
});

RevenueCalculator.displayName = 'RevenueCalculator';
